<template>
  <div
      id="enquiry-form"
      class="enquiry-form"
  >
    <div v-if="form.step == 1" class="enquiry-form__step1 row">
      <div class="enquiry-form__form-wrapper col-10 offset-1 col-lg-4 offset-lg-1 np--all">
        <p class="enquiry-form__location">{{ mainHeader }}</p>
        <p class="enquiry-form__body text--faux">
          {{ primaryText }}
        </p>
        <p class="enquiry-form__body text--faux">
          {{ secondaryText }}
        </p>
        <form class="field--dark" method="post" role="form">
          <div class="enquiry-form__step1--details row">
            <fieldset>
              <div class="form-wrapper row">
                <input-text
                    ref="name"
                    v-model="values.name"
                    class="col-md-6 col-lg-10 col-xl-8 np--all"
                    label="Name"
                    autocomplete-value="name"
                    error-message="Please enter your name"
                    validate="required|alpha_spaces"
                    name="NAME"
                    type="text"
                    @input="validate"
                />
                <input-text
                    ref="phone"
                    v-model="values.phone"
                    class="col-md-6 col-lg-10 col-xl-8 np--all"
                    label="Phone Number"
                    autocomplete-value="tel"
                    error-message="Please enter a valid phone number: the value should be a number and add + if country code is needed"
                    validate="required|phoneNumber"
                    name="PHONE"
                    type="tel"
                    @input="validate"
                />
                <input-text
                    ref="email"
                    v-model="values.email"
                    class="col-lg-10 col-xl-8 np--all"
                    label="Email"
                    autocomplete-value="email"
                    error-message="Please enter your email address in format: yourname@example.com"
                    validate="required|email"
                    name="EMAIL"
                    type="email"
                    @input="validate"
                />
              </div>

              <div class="form-wrapper row">
                <div class="input-icon-wrapper col-md-6 col-lg-10 col-xl-8 np--all">
                  <input-select
                      id="v-select"
                      ref="selectedTime"
                      v-model="values.selectedTime"
                      :options="form.times"
                      :searchable="false"
                      :clearable="false"
                      label="Preferred contact time"
                      name="TIME"
                      validate="required"
                      error-message="Please pick a preferred time"
                      @input="validate"
                  />
                  <Clock class="enquiry-form__clock" />
                </div>

                <div class="input-icon-wrapper col-md-6 col-lg-10 col-xl-8 np--all">
                  <input-select
                      id="v-select"
                      ref="selectedMethod"
                      v-model="values.selectedMethod"
                      :options="form.contactMethods"
                      :searchable="false"
                      :clearable="false"
                      label="Preferred contact method"
                      error-message="Please pick a preferred contact method"
                      validate="required"
                      name="METHOD"
                      @input="validate"
                  />
                </div>
              </div>
              <div class="form-wrapper row">
                <input-textarea
                    ref="preferred_location"
                    v-model="values.preferred_location"
                    class="enquiry-form__textarea--pLocation col-lg-10 col-xl-8 np--all"
                    label="Preferred Location"
                    validate="required"
                    name="PREFERRED_LOCATION"
                    error-message="Please enter your preferred location"
                    @input="validate"
                />
                <input-textarea
                    ref="comments"
                    v-model="values.comments"
                    class="enquiry-form__textarea col-lg-10 col-xl-8 np--all"
                    label="Comments - optional"
                    name="COMMENTS"
                    @input="validate"
                />
              </div>
            </fieldset>
          </div>

          <div class="enquiry-form__button col-12 np--all">
            <button :disabled="form.submitDisabled" class="btn--dark-green" type="submit" @click.prevent="submit">
              Submit<Arrow class="btn--arrow__icon btn--arrow__icon--orange" />
            </button>
          </div>
        </form>
      </div>

      <div class="col-lg-7">
        <div class="enquiry-form__image-container">
          <figure class="enquiry-form__image">
            <KenticoResponsiveImage :src="imageUrl" :alt="imageAlt" class="booking-shape" :sizes="imageSizes" />
          </figure>
          <BookingTourShape />
          <ShapeYellow class="enquiry-form__background-shape" />
        </div>
      </div>
    </div>

    <div role="status">
      <div v-if="form.step == 2" class="enquiry-form__step2 row" aria-live="polite">
        <div class="enquiry-form__thankyou-wrapper col-10 offset-1 col-lg-4 offset-lg-1">
          <p class="enquiry-form__thankyou-title">{{ form.thankyouTitle }}</p>
          <p class="enquiry-form__thankyou-message text--x-small text--faux" v-html="form.thankyouMessage" />
          <div class="enquiry-form__homepage-button">
            <button class="btn--dark-green" type="submit" aria-label="Back to home" @click.prevent="close">
              Home<Arrow class="btn--arrow__icon btn--arrow__icon--orange" />
            </button>
          </div>
        </div>
        <div class="col-lg-7">
          <div class="enquiry-form__image-container">
            <figure class="enquiry-form__image">
              <KenticoResponsiveImage :src="imageUrl" :alt="imageAlt" class="booking-shape" :sizes="imageSizes" />
            </figure>
            <BookingTourShape />
            <ShapeYellow class="enquiry-form__background-shape" />
          </div>
        </div>
      </div>
    </div>

    <div class="enquiry-form__shape-wrapper">
      <GreenShape class="enquiry-form__green-shape" />
      <BlueShape class="enquiry-form__blue-shape" />
    </div>
  </div>
</template>

<script>
import BookingTourShape from '~/components/elements/BookingTourShape.vue';
import ShapeYellow from '~/assets/images/booking/shape-yellow.svg?inline';
import GreenShape from '~/assets/images/shapes/shape-green-rectangle.svg?inline';
import BlueShape from '~/assets/images/shapes/shape-blue-circle.svg?inline';
import { focusElements } from '~/components/mixins/focus-elements.js';
import InputText from '~/components/elements/InputText';
import InputTextarea from '~/components/elements/InputTextarea';
import InputSelect from '~/components/elements/InputSelect';
import Clock from '~/assets/icons/clock.svg?inline';
import { BREAKPOINTS, BREAKPOINT_XS, BREAKPOINT_MD, BREAKPOINT_LG } from '~/utils/constants';
import { trackEnquiry } from '~/utils/gtm-events.js';

const util = require('util');

export default {
  components: {
    BookingTourShape,
    ShapeYellow,
    GreenShape,
    BlueShape,
    InputText,
    InputTextarea,
    InputSelect,
    Clock,
  },
  mixins: [focusElements],
  props: {
    title: { type: String, default: '' },
    mainHeader: { type: String, default: '' },
    primaryText: { type: String, default: '' },
    secondaryText: { type: String, default: '' },
    enquiryImage: { type: Array, default: null },
  },
  data() {
    return {
      imageSizes: {
        default: [900, 750],
        sizes: [
          { breakpoint: BREAKPOINTS[BREAKPOINT_XS], width: 900, height: 750 },
          { breakpoint: BREAKPOINTS[BREAKPOINT_MD], width: 900, height: 750 },
          { breakpoint: BREAKPOINTS[BREAKPOINT_LG], width: 1080, height: 900 },
        ],
      },
      // Form values, which will be sent to formkeeper
      values: {
        preferred_location: '',
        name: null,
        phone: null,
        email: null,
        comments: null,
        created_at: new Date(),
        selectedTime: null,
        selectedMethod: null,
      },
      // Form state
      form: {
        step: 1,
        submitDisabled: true,
        thankyouTitle: 'Thank you!',
        thankyouMessage:
          '<p>We\’ll be in touch very shortly. If you have any questions in the meantime, please email us at <a href="mailto:domesales@thecollective.com">domesales@thecollective.com</a>.</p>',
        times: [
          {
            label: 'Morning',
            value: 'Morning',
          },
          {
            label: 'Afternoon',
            value: 'Afternoon',
          },
          {
            label: 'Evening',
            value: 'Evening',
          },
        ],
        contactMethods: [
          {
            label: 'Email',
            value: 'Email',
          },
          {
            label: 'Phone',
            value: 'Phone',
          },
        ],
      },
    };
  },
  computed: {
    imageUrl() {
      return this.enquiryImage && this.enquiryImage.length > 0 ? this.enquiryImage[0].url : '';
    },
    imageAlt() {
      return this.enquiryImage && this.enquiryImage.length > 0 ? this.enquiryImage[0].description ? this.enquiryImage[0].description : '' : '';
    },
  },
  mounted() {
    this.reset();
  },
  methods: {
    validate() {
      this.form.submitDisabled = true;

      if (this.$refs.name.isValid() &&
          this.$refs.phone.isValid() &&
          this.$refs.email.isValid() &&
          this.$refs.selectedMethod.hasValue &&
          this.$refs.selectedTime.hasValue &&
          this.$refs.preferred_location.isValid()) {
        this.form.submitDisabled = false;
        return true;
      } else {
        return false;
      }
    },
    submit() {
      if (this.validate()) {
        this.$axios.post(process.env.ENQUIRY_FORM_ENDPOINT, this.values, {
          headers: { Accept: 'application/javascript' },
        })
            .then((response) => {
            // console.log(response);

              trackEnquiry(this.$gtm, this.values);
              console.log(util.inspect(response, { showHidden: false, depth: null }));
              this.form.step = 2;
            })
            .catch((error) => {
            // console.error(error);
              console.error(util.inspect(error, { showHidden: false, depth: null }));
              this.form.step = 2;
            });
      }
    },
    close() {
      this.$router.push({ path: '/serviced-living' });
    },
    reset() {
      this.form.submitDisabled = true;
      this.values = {
        preferred_location: '',
        name: null,
        phone: null,
        email: null,
        comments: null,
        created_at: new Date(),
        selectedTime: null,
        selectedMethod: null,
      };

      this.form.step = 1;

    },
  },
};
</script>

<style lang="scss">
@import '~/assets/scss/atomic/modules/_enquiry-form.scss';
</style>
